import { Reveal, Eyebrow } from "@/components/Reveal";
import { Check, ShieldCheck, Bug, PenLine, DatabaseBackup, LifeBuoy } from "lucide-react";
import { scrollToId } from "@/lib/api";

const SCOPE = [
  { icon: PenLine, text: "Ongoing content updates — text, images, pages and announcements" },
  { icon: Bug, text: "Bug fixes and continuous monitoring so issues are caught early" },
  { icon: ShieldCheck, text: "Security checks and updates, where your platform supports them" },
  { icon: DatabaseBackup, text: "Regular backup checks, where your hosting supports them" },
  { icon: LifeBuoy, text: "Direct technical support over email and WhatsApp" },
];

export default function Maintenance({ plans, appPlans = [] }) {
  return (
    <section id="maintenance" className="py-24 lg:py-32" data-testid="maintenance-section">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid items-start gap-16 lg:grid-cols-2">
          <div>
            <Reveal>
              <Eyebrow>Website Maintenance</Eyebrow>
              <h2 className="font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
                Your website, looked after — month after month.
              </h2>
              <p className="mt-6 text-base leading-relaxed text-slate-400 sm:text-lg">
                A website is never really "done". Our monthly care plans keep yours updated, secure
                and improving, so you can focus on running your business while we handle the
                technology.
              </p>
            </Reveal>
            <Reveal delay={0.12}>
              <ul className="mt-9 space-y-5">
                {SCOPE.map((s) => (
                  <li key={s.text} className="flex items-start gap-4">
                    <span className="rounded-xl border border-white/10 bg-white/5 p-2.5 text-[#00F0FF]">
                      <s.icon size={18} />
                    </span>
                    <span className="pt-1.5 text-sm leading-relaxed text-slate-300">{s.text}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <div className="space-y-5">
            {plans.map((plan, i) => (
              <Reveal key={plan.id} delay={i * 0.1}>
                <div
                  data-testid={`maintenance-plan-${plan.id}`}
                  className={`rounded-3xl border p-7 transition-transform duration-300 hover:-translate-y-1 ${
                    i === 1 ? "glow-card border-[#00F0FF]/50 bg-[#0A0F1C]" : "border-white/10 bg-[#0A0F1C]"
                  }`}
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h3 className="font-display text-lg font-bold text-white">{plan.name}</h3>
                    <p>
                      <span className="font-display text-3xl font-black text-white">{plan.price}</span>
                      <span className="text-sm text-slate-500">{plan.period}</span>
                    </p>
                  </div>
                  <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-xs text-slate-300">
                        <Check size={14} className="mt-0.5 shrink-0 text-[#00F0FF]" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <button
                data-testid="maintenance-cta-btn"
                onClick={() => scrollToId("contact")}
                className="w-full rounded-full bg-[#00F0FF] px-6 py-3.5 text-sm font-bold text-[#050811] transition-shadow duration-300 hover:shadow-[0_0_28px_rgba(0,240,255,0.55)]"
              >
                Ask about a maintenance plan
              </button>
            </Reveal>
          </div>
        </div>

        {appPlans.length > 0 && (
          <div className="mt-20" data-testid="app-care-plans">
            <Reveal className="max-w-2xl">
              <h3 className="font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
                App Care Plans
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400 sm:text-base">
                For apps we build — monthly care that keeps your application healthy, updated and
                improving after delivery.
              </p>
            </Reveal>
            <div className="mt-8 grid gap-5 md:grid-cols-3">
              {appPlans.map((plan, i) => (
                <Reveal key={plan.id} delay={i * 0.1}>
                  <div
                    data-testid={`app-care-plan-${plan.id}`}
                    className={`h-full rounded-3xl border p-7 transition-transform duration-300 hover:-translate-y-1 ${
                      i === 1 ? "glow-card border-[#00F0FF]/50 bg-[#0A0F1C]" : "border-white/10 bg-[#0A0F1C]"
                    }`}
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <h4 className="font-display text-base font-bold text-white">{plan.name}</h4>
                      <p>
                        <span className="font-display text-2xl font-black text-white">{plan.price}</span>
                        <span className="text-xs text-slate-500">{plan.period}</span>
                      </p>
                    </div>
                    <ul className="mt-5 space-y-2.5">
                      {plan.features.map((f) => (
                        <li key={f} className="flex items-start gap-2.5 text-xs text-slate-300">
                          <Check size={14} className="mt-0.5 shrink-0 text-[#00F0FF]" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
